import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Building2, Users2, Bell, Megaphone, FileText, Save, Loader2,
  Plus, Trash2, AlertTriangle, History as HistoryIcon,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import api, { formatErrorMessage } from '../utils/api';
import { toast } from 'sonner';
import { isSocio } from '../utils/permissions';

const emptyConfig = {
  company_info: {
    razon_social: '', nombre_comercial: '', rut: '', direccion: '',
    ciudad: '', telefono_contacto: '', email_contacto: '', web: '',
  },
  socios: [],
  alertas: {
    comercial_alerta_emails: [],
    cobranza_email_remitente: '',
    cobranza_nombre_remitente: '',
  },
  asistentes_reunion_socios: [],
  brain_learning_pesos: [],
  branding: {
    whatsapp: '', web: '', cta_frase: '', tono_marca: '', firma_marca: '',
  },
  pdf_footer: { linea_titulo: '', linea_contacto: '' },
};

export default function CompanyConfigTab({ currentUser }) {
  const [config, setConfig] = useState(emptyConfig);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState([]);

  const puedeEditar = isSocio(currentUser);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/company-config');
      // asegurar estructura completa
      setConfig({
        ...emptyConfig,
        ...data,
        company_info: { ...emptyConfig.company_info, ...(data.company_info || {}) },
        alertas: { ...emptyConfig.alertas, ...(data.alertas || {}) },
        branding: { ...emptyConfig.branding, ...(data.branding || {}) },
        pdf_footer: { ...emptyConfig.pdf_footer, ...(data.pdf_footer || {}) },
        socios: data.socios || [],
        asistentes_reunion_socios: data.asistentes_reunion_socios || [],
        brain_learning_pesos: data.brain_learning_pesos || [],
      });
    } catch (err) {
      toast.error(formatErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const guardar = async () => {
    if (!puedeEditar) return;
    setSaving(true);
    try {
      const payload = {
        company_info: config.company_info,
        socios: config.socios,
        alertas: config.alertas,
        asistentes_reunion_socios: config.asistentes_reunion_socios,
        brain_learning_pesos: config.brain_learning_pesos,
        branding: config.branding,
        pdf_footer: config.pdf_footer,
      };
      const { data } = await api.put('/company-config', payload);
      const changed = data.secciones_modificadas || [];
      if (changed.length === 0) {
        toast.info('Sin cambios para guardar');
      } else {
        toast.success(`Guardado. Secciones actualizadas: ${changed.join(', ')}`);
      }
    } catch (err) {
      toast.error(formatErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const cargarAudit = async () => {
    try {
      const { data } = await api.get('/company-config/audit');
      setAudit(data.items || []);
      setAuditOpen(true);
    } catch (err) {
      toast.error(formatErrorMessage(err));
    }
  };

  // ---- helpers de edición ----
  const setInfo = (k, v) => setConfig(c => ({ ...c, company_info: { ...c.company_info, [k]: v } }));
  const setBrand = (k, v) => setConfig(c => ({ ...c, branding: { ...c.branding, [k]: v } }));
  const setFooter = (k, v) => setConfig(c => ({ ...c, pdf_footer: { ...c.pdf_footer, [k]: v } }));
  const setAlerta = (k, v) => setConfig(c => ({ ...c, alertas: { ...c.alertas, [k]: v } }));

  const addSocio = () => setConfig(c => ({
    ...c, socios: [...c.socios, { name: '', email: '', rut: '', cargo: 'Socio' }]
  }));
  const rmSocio = (i) => setConfig(c => ({ ...c, socios: c.socios.filter((_, idx) => idx !== i) }));
  const editSocio = (i, k, v) => setConfig(c => ({
    ...c, socios: c.socios.map((s, idx) => idx === i ? { ...s, [k]: v } : s)
  }));

  const addAsistente = () => setConfig(c => ({
    ...c, asistentes_reunion_socios: [...c.asistentes_reunion_socios, { email: '', name: '', cargo: '' }]
  }));
  const rmAsistente = (i) => setConfig(c => ({
    ...c, asistentes_reunion_socios: c.asistentes_reunion_socios.filter((_, idx) => idx !== i)
  }));
  const editAsistente = (i, k, v) => setConfig(c => ({
    ...c, asistentes_reunion_socios: c.asistentes_reunion_socios.map((a, idx) => idx === i ? { ...a, [k]: v } : a)
  }));

  const addPeso = () => setConfig(c => ({
    ...c, brain_learning_pesos: [...c.brain_learning_pesos, { email: '', peso: 1.5, nombre: '' }]
  }));
  const rmPeso = (i) => setConfig(c => ({
    ...c, brain_learning_pesos: c.brain_learning_pesos.filter((_, idx) => idx !== i)
  }));
  const editPeso = (i, k, v) => setConfig(c => ({
    ...c, brain_learning_pesos: c.brain_learning_pesos.map((p, idx) => idx === i ? { ...p, [k]: k === 'peso' ? parseFloat(v) || 0 : v } : p)
  }));

  if (loading) {
    return (
      <Card className="p-8 flex items-center justify-center gap-2 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando configuración corporativa…
      </Card>
    );
  }

  return (
    <Card className="p-6 border-slate-200" data-testid="company-config-tab">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h3 className="font-semibold text-slate-900 flex items-center gap-2 text-lg">
            <Building2 className="w-5 h-5 text-primary" />
            Configuración Corporativa
          </h3>
          <p className="text-sm text-slate-500 mt-1">
            Datos de la empresa, socios, destinatarios de alertas y branding. Reemplaza los valores previamente hardcodeados en el código (contratos PDF, footer, correos automáticos, etc.).
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={cargarAudit} data-testid="company-config-audit-btn">
            <HistoryIcon className="w-4 h-4 mr-1" /> Historial
          </Button>
          <Button
            onClick={guardar}
            disabled={saving || !puedeEditar}
            data-testid="company-config-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Guardar Cambios
          </Button>
        </div>
      </div>

      {!puedeEditar && (
        <div className="mb-4 flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded p-3 text-sm">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          Solo los Socios pueden modificar esta configuración. Puedes ver los valores actuales en modo lectura.
        </div>
      )}

      {/* ==================== SECCIÓN: EMPRESA ==================== */}
      <Section title="Información de la Empresa" icon={<Building2 className="w-4 h-4" />}>
        <Grid2>
          <Field label="Razón Social" testid="cfg-razon-social">
            <Input value={config.company_info.razon_social} onChange={e => setInfo('razon_social', e.target.value)} disabled={!puedeEditar} />
          </Field>
          <Field label="Nombre Comercial" testid="cfg-nombre-comercial">
            <Input value={config.company_info.nombre_comercial || ''} onChange={e => setInfo('nombre_comercial', e.target.value)} disabled={!puedeEditar} />
          </Field>
          <Field label="RUT" testid="cfg-rut">
            <Input value={config.company_info.rut} onChange={e => setInfo('rut', e.target.value)} disabled={!puedeEditar} />
          </Field>
          <Field label="Ciudad" testid="cfg-ciudad">
            <Input value={config.company_info.ciudad || ''} onChange={e => setInfo('ciudad', e.target.value)} disabled={!puedeEditar} />
          </Field>
          <Field label="Teléfono de Contacto" testid="cfg-telefono">
            <Input value={config.company_info.telefono_contacto || ''} onChange={e => setInfo('telefono_contacto', e.target.value)} disabled={!puedeEditar} />
          </Field>
          <Field label="Email de Contacto" testid="cfg-email">
            <Input value={config.company_info.email_contacto || ''} onChange={e => setInfo('email_contacto', e.target.value)} disabled={!puedeEditar} />
          </Field>
          <Field label="Sitio Web" testid="cfg-web">
            <Input value={config.company_info.web || ''} onChange={e => setInfo('web', e.target.value)} disabled={!puedeEditar} />
          </Field>
          <Field label="Dirección Legal (usada en contratos)" full testid="cfg-direccion">
            <Textarea rows={2} value={config.company_info.direccion} onChange={e => setInfo('direccion', e.target.value)} disabled={!puedeEditar} />
          </Field>
        </Grid2>
      </Section>

      {/* ==================== SECCIÓN: SOCIOS ==================== */}
      <Section
        title="Socios (usados en contratos PDF)"
        icon={<Users2 className="w-4 h-4" />}
        action={puedeEditar && (
          <Button variant="outline" size="sm" onClick={addSocio} data-testid="cfg-add-socio">
            <Plus className="w-4 h-4 mr-1" /> Agregar socio
          </Button>
        )}
      >
        {config.socios.length === 0 ? (
          <EmptyRow msg="Sin socios definidos" />
        ) : (
          <div className="space-y-2">
            {config.socios.map((s, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`cfg-socio-row-${i}`}>
                <div className="col-span-4">
                  <Label className="text-xs">Nombre completo</Label>
                  <Input value={s.name} onChange={e => editSocio(i, 'name', e.target.value)} disabled={!puedeEditar} />
                </div>
                <div className="col-span-3">
                  <Label className="text-xs">RUT</Label>
                  <Input value={s.rut} onChange={e => editSocio(i, 'rut', e.target.value)} disabled={!puedeEditar} />
                </div>
                <div className="col-span-3">
                  <Label className="text-xs">Email</Label>
                  <Input value={s.email || ''} onChange={e => editSocio(i, 'email', e.target.value)} disabled={!puedeEditar} />
                </div>
                <div className="col-span-1">
                  <Label className="text-xs">Cargo</Label>
                  <Input value={s.cargo || ''} onChange={e => editSocio(i, 'cargo', e.target.value)} disabled={!puedeEditar} />
                </div>
                <div className="col-span-1 flex justify-end">
                  {puedeEditar && (
                    <Button variant="ghost" size="sm" onClick={() => rmSocio(i)} data-testid={`cfg-rm-socio-${i}`}>
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* ==================== SECCIÓN: ALERTAS Y COBRANZA ==================== */}
      <Section title="Emails de Alertas y Cobranza" icon={<Bell className="w-4 h-4" />}>
        <Grid2>
          <Field label="Emails Alerta Comercial (separados por coma)" full testid="cfg-alerta-emails">
            <Input
              value={(config.alertas.comercial_alerta_emails || []).join(', ')}
              onChange={e => setAlerta('comercial_alerta_emails', e.target.value.split(',').map(x => x.trim()).filter(Boolean))}
              disabled={!puedeEditar}
              placeholder="ej: gcarrasco@peritum.cl, gflores@peritum.cl"
            />
            <p className="text-xs text-slate-500 mt-1">Destinatarios de la alerta 09:30 AM de reuniones con socios.</p>
          </Field>
          <Field label="Email Remitente Cobranzas" testid="cfg-cobranza-email">
            <Input value={config.alertas.cobranza_email_remitente} onChange={e => setAlerta('cobranza_email_remitente', e.target.value)} disabled={!puedeEditar} />
          </Field>
          <Field label="Nombre Remitente Cobranzas" testid="cfg-cobranza-nombre">
            <Input value={config.alertas.cobranza_nombre_remitente} onChange={e => setAlerta('cobranza_nombre_remitente', e.target.value)} disabled={!puedeEditar} />
          </Field>
        </Grid2>
      </Section>

      {/* ==================== SECCIÓN: ASISTENTES REUNIONES ==================== */}
      <Section
        title="Asistentes Reunión con Socios"
        icon={<Users2 className="w-4 h-4" />}
        action={puedeEditar && (
          <Button variant="outline" size="sm" onClick={addAsistente} data-testid="cfg-add-asistente">
            <Plus className="w-4 h-4 mr-1" /> Agregar asistente
          </Button>
        )}
      >
        {config.asistentes_reunion_socios.length === 0 ? (
          <EmptyRow msg="Sin asistentes definidos" />
        ) : (
          <div className="space-y-2">
            {config.asistentes_reunion_socios.map((a, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`cfg-asistente-row-${i}`}>
                <div className="col-span-4">
                  <Label className="text-xs">Nombre</Label>
                  <Input value={a.name} onChange={e => editAsistente(i, 'name', e.target.value)} disabled={!puedeEditar} />
                </div>
                <div className="col-span-4">
                  <Label className="text-xs">Email</Label>
                  <Input value={a.email} onChange={e => editAsistente(i, 'email', e.target.value)} disabled={!puedeEditar} />
                </div>
                <div className="col-span-3">
                  <Label className="text-xs">Cargo</Label>
                  <Input value={a.cargo || ''} onChange={e => editAsistente(i, 'cargo', e.target.value)} disabled={!puedeEditar} />
                </div>
                <div className="col-span-1 flex justify-end">
                  {puedeEditar && (
                    <Button variant="ghost" size="sm" onClick={() => rmAsistente(i)} data-testid={`cfg-rm-asistente-${i}`}>
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* ==================== SECCIÓN: BRAIN LEARNING PESOS ==================== */}
      <Section
        title="Cerebro — Usuarios con Peso Especial"
        icon={<Users2 className="w-4 h-4" />}
        action={puedeEditar && (
          <Button variant="outline" size="sm" onClick={addPeso} data-testid="cfg-add-peso">
            <Plus className="w-4 h-4 mr-1" /> Agregar
          </Button>
        )}
      >
        {config.brain_learning_pesos.length === 0 ? (
          <EmptyRow msg="Sin pesos especiales" />
        ) : (
          <div className="space-y-2">
            {config.brain_learning_pesos.map((p, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`cfg-peso-row-${i}`}>
                <div className="col-span-5">
                  <Label className="text-xs">Email</Label>
                  <Input value={p.email} onChange={e => editPeso(i, 'email', e.target.value)} disabled={!puedeEditar} />
                </div>
                <div className="col-span-4">
                  <Label className="text-xs">Nombre</Label>
                  <Input value={p.nombre || ''} onChange={e => editPeso(i, 'nombre', e.target.value)} disabled={!puedeEditar} />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Peso</Label>
                  <Input type="number" step="0.1" value={p.peso} onChange={e => editPeso(i, 'peso', e.target.value)} disabled={!puedeEditar} />
                </div>
                <div className="col-span-1 flex justify-end">
                  {puedeEditar && (
                    <Button variant="ghost" size="sm" onClick={() => rmPeso(i)} data-testid={`cfg-rm-peso-${i}`}>
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* ==================== SECCIÓN: BRANDING ==================== */}
      <Section title="Branding & Redes Sociales" icon={<Megaphone className="w-4 h-4" />}>
        <Grid2>
          <Field label="WhatsApp (con código país)" testid="cfg-branding-wsp">
            <Input value={config.branding.whatsapp || ''} onChange={e => setBrand('whatsapp', e.target.value)} disabled={!puedeEditar} />
          </Field>
          <Field label="Sitio Web (branding)" testid="cfg-branding-web">
            <Input value={config.branding.web || ''} onChange={e => setBrand('web', e.target.value)} disabled={!puedeEditar} />
          </Field>
          <Field label="Frase CTA base" full testid="cfg-branding-cta">
            <Input value={config.branding.cta_frase || ''} onChange={e => setBrand('cta_frase', e.target.value)} disabled={!puedeEditar} />
          </Field>
          <Field label="Tono de marca" full testid="cfg-branding-tono">
            <Textarea rows={2} value={config.branding.tono_marca || ''} onChange={e => setBrand('tono_marca', e.target.value)} disabled={!puedeEditar} />
          </Field>
          <Field label="Firma de marca" full testid="cfg-branding-firma">
            <Input value={config.branding.firma_marca || ''} onChange={e => setBrand('firma_marca', e.target.value)} disabled={!puedeEditar} />
          </Field>
        </Grid2>
      </Section>

      {/* ==================== SECCIÓN: PDF FOOTER ==================== */}
      <Section title="Footer PDFs (Estados de Cuenta, etc.)" icon={<FileText className="w-4 h-4" />}>
        <Grid2>
          <Field label="Línea 1 (Título)" full testid="cfg-footer-titulo">
            <Input value={config.pdf_footer.linea_titulo} onChange={e => setFooter('linea_titulo', e.target.value)} disabled={!puedeEditar} />
          </Field>
          <Field label="Línea 2 (Contacto: email · teléfono)" full testid="cfg-footer-contacto">
            <Input value={config.pdf_footer.linea_contacto} onChange={e => setFooter('linea_contacto', e.target.value)} disabled={!puedeEditar} />
          </Field>
        </Grid2>
      </Section>

      {/* ==================== HISTORIAL DIALOG ==================== */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Historial de cambios — Configuración Corporativa</DialogTitle>
            <DialogDescription>Últimas 50 modificaciones registradas en la configuración corporativa.</DialogDescription>
          </DialogHeader>
          {audit.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">Sin cambios registrados</p>
          ) : (
            <div className="space-y-3">
              {audit.map((a) => (
                <Card key={a.id} className="p-3 border-slate-200 text-sm" data-testid={`audit-item-${a.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold text-slate-800">{a.by_name || a.by_email}</div>
                    <div className="text-xs text-slate-500">{new Date(a.at).toLocaleString('es-CL')}</div>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(a.secciones_modificadas || []).map(s => (
                      <Badge key={s} variant="secondary">{s}</Badge>
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAuditOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ==================== SUB-COMPONENTES ====================
function Section({ title, icon, action, children }) {
  return (
    <div className="mt-6 border-t border-slate-200 pt-5">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
          {icon}{title}
        </h4>
        {action}
      </div>
      {children}
    </div>
  );
}

function Grid2({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, children, full, testid }) {
  return (
    <div className={full ? 'md:col-span-2' : ''} data-testid={testid}>
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

function EmptyRow({ msg }) {
  return <p className="text-sm text-slate-400 italic py-3">{msg}</p>;
}
